import { countryTelMap } from '@shared/intlConstants';
import { convertNumerals } from './number';

/*
  Map of countries which take precedence over other countries with the same phone prefix
  This can be obtained by running the following code on the data in `intl-tel-input`
  (https://github.com/jackocnr/intl-tel-input/blob/master/src/js/data.js):
  console.log(JSON.stringify(allCountries.filter(c => c[3] === 0)
    .reduce((a,c)=> ({ ...a, [c[1].toUpperCase()]:c[2]}),{}), undefined, 2))
*/
const prioritizedCountries: Record<string, string> = {
  AU: '61',
  CW: '599',
  FI: '358',
  GB: '44',
  GP: '590',
  IT: '39',
  MA: '212',
  NO: '47',
  RE: '262',
  RU: '7',
  US: '1',
};

const telCountryMap = Object.fromEntries(
  [...Object.entries(countryTelMap), ...Object.entries(prioritizedCountries)].map(([code, tel]) => [
    tel,
    code,
  ]),
);

export const sanitizePhoneNumber = (number: string) =>
  convertNumerals(number)
    .replace(/^00/, '+')
    // replace anything but digits and +'s
    .replace(/[^\d+]/g, '')
    // replace all +'s except the first one
    .replace(/^(\+?)(.+)$/g, (_, plus, num) => `${plus}${num.replace(/\+/g, '')}`);

export const findCountryByPhone = (phone: string) => {
  const normalizedPhone = convertNumerals(phone).replace(/^00/, '+');
  if (!normalizedPhone.startsWith('+')) {
    return '';
  }
  for (let i = 3; i > 0; --i) {
    const found = telCountryMap[normalizedPhone.slice(1, i + 1)];
    if (found) {
      return found;
    }
  }
  return '';
};

const countryValidationRules = {
  ['SA' as string]: /^5[0-9]{8}$/,
};

export const validatePhoneForCountry = (phone?: string | null) => {
  if (!phone) {
    return true;
  }
  const country = findCountryByPhone(phone);
  if (!country || !countryTelMap[country]) {
    return false;
  }
  const rule = countryValidationRules[country];
  if (!rule) {
    return true;
  }

  const barePhone = convertNumerals(phone).replace(`+${countryTelMap[country]}`, '');
  return rule.test(barePhone);
};
